import { useCallback, useId, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverClose, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { BotonFiltros } from './boton-filtros';
import { SeccionContexto } from './seccion-contexto';
import type { FiltroSeccionProps, FiltrosPopoverProps } from './tipos';

// Popover «Filtros» (pieza compartida, Ajuste #16). Receta: relevamiento de la
// página §2 (artifact `.pop`, radio 12, `shadow-pop`, pop-in de 140 ms) y
// síntesis de la home §2 («Tablet» y «Teclado y a11y»):
// - 400 px (el artifact tenía 360; verificación visual de T3b §4.3: a 360 no
//   entraba en la ventana de Juan, 1440×694) y `align="end"` contra el botón
//   (§13 1.1);
// - cabecera de UNA línea: «Filtros», «+ N de la búsqueda» al lado (no debajo:
//   la cabecera no crece cuando aparece) y «Limpiar» a la derecha;
// - secciones compactas (título a 4 px de su grupo, 12 px entre un grupo y el
//   título siguiente) para que TODO entre sin scroll a 1440×694 y 1280×800;
// - cuerpo con SCROLL INTERNO y `max-h` = `--available-height` de Base UI, solo
//   para cuando igual no entra (tablet 1024×768 táctil, con 44 px por opción):
//   el pie queda a la vista;
// - pie «N … con estos filtros · Listo», todo en terciario, sin región viva;
// - foco al abrir: con teclado, el primer control del primer grupo; con toque o
//   mouse, el popup (un Enter por reflejo no cae en «Limpiar»). Al cerrar, Base
//   UI devuelve el foco al botón. «Limpiar» no se deshabilita de verdad
//   (`aria-disabled`): el foco no se cae a `<body>` y, al limpiar, pasa al
//   primer control del cuerpo (juez #2 de T3b).
// Nada del dominio adentro: secciones, textos y conteos los pasa la página.

const fmt = new Intl.NumberFormat('es-AR', { maximumFractionDigits: 0 });

/** Si no entra abajo, el popover NO salta al costado del botón (el default de
 *  Base UI cae al eje perpendicular): queda abajo (o arriba, si ahí entra
 *  mejor) con el alto recortado a `--available-height` y el cuerpo scrollea. */
const EVITAR_COLISION = { side: 'flip', align: 'shift', fallbackAxisSide: 'none' } as const;

/** Lo que cuenta como «control» para el foco inicial con teclado: el tab stop
 *  de cada grupo (Base UI deja `tabIndex=0` solo en uno por grupo). */
function primerEnfocable(raiz: HTMLElement): HTMLElement | null {
  const candidatos = raiz.querySelectorAll<HTMLElement>(
    'button, input, select, textarea, a[href], [role="radio"], [role="checkbox"], [tabindex]',
  );
  for (const el of candidatos) {
    if (el.tabIndex < 0) continue;
    if (el.hasAttribute('disabled') || el.getAttribute('aria-disabled') === 'true') continue;
    if (el instanceof HTMLInputElement && el.type === 'hidden') continue;
    return el;
  }
  return null;
}

export function FiltrosPopover({
  abierto,
  onAbiertoChange,
  nActivos,
  ariaLabel,
  puedeLimpiar,
  onLimpiar,
  nBusqueda,
  pie,
  children,
  botonProps,
}: FiltrosPopoverProps) {
  const cuerpoRef = useRef<HTMLDivElement>(null);

  // `initialFocus` en su forma función (síntesis §2): Base UI la llama con el
  // tipo de interacción que abrió el popover.
  const focoInicial = useCallback((tipo: string): HTMLElement | boolean => {
    const cuerpo = cuerpoRef.current;
    if (!cuerpo) return true;
    if (tipo === 'keyboard') {
      const primero = primerEnfocable(cuerpo);
      if (primero) return primero;
    }
    return cuerpo.closest<HTMLElement>('[data-slot="popover-content"]') ?? true;
  }, []);

  // «Limpiar» queda `aria-disabled` después de limpiar (ya no hay nada): el
  // foco pasa al primer control del cuerpo, ya re-renderizado (el próximo frame).
  // El cuerpo se REMONTA al limpiar (key): los RadioGroup de Base UI no
  // resincronizan su tab stop cuando el valor cambia desde afuera, y quedaba el
  // foco en un radio sin marcar (review del fix de T3b, #2). En ese momento el
  // foco está en «Limpiar», fuera del cuerpo, así que no se pierde.
  const [nLimpiezas, setNLimpiezas] = useState(0);
  const limpiar = () => {
    if (!puedeLimpiar) return;
    onLimpiar();
    setNLimpiezas(n => n + 1);
    requestAnimationFrame(() => {
      const cuerpo = cuerpoRef.current;
      if (!cuerpo) return;
      const primero = primerEnfocable(cuerpo);
      const marcado = primero
        ?.closest('[role="radiogroup"]')
        ?.querySelector<HTMLElement>('[role="radio"][aria-checked="true"]');
      (marcado ?? primero)?.focus();
    });
  };

  return (
    <Popover open={abierto} onOpenChange={next => onAbiertoChange(next)}>
      <PopoverTrigger render={<BotonFiltros nActivos={nActivos} {...botonProps} />} />
      <PopoverContent
        align="end"
        collisionAvoidance={EVITAR_COLISION}
        aria-label={ariaLabel}
        initialFocus={focoInicial}
        className={cn(
          'flex max-h-[var(--available-height)] w-[400px] max-w-[calc(100vw-24px)] flex-col overflow-hidden rounded-[12px] p-0',
          // text-* antes que leading-* (cn resuelve el conflicto a favor del último).
          'text-[13px] leading-normal',
          'shadow-[0_18px_40px_-12px_rgba(20,20,22,0.22),0_2px_6px_rgba(20,20,22,0.06)] dark:shadow-[0_18px_44px_-12px_rgba(0,0,0,0.7),0_2px_6px_rgba(0,0,0,0.4)]',
          'duration-[140ms] motion-reduce:animate-none!',
        )}
      >
        <div className="flex min-h-7 flex-none items-baseline gap-2 border-b border-border-subtle px-3 pb-1.5 pt-2">
          <h2 className="m-0 flex-none text-[13px] font-[650] text-foreground">Filtros</h2>
          {nBusqueda > 0 && (
            // Los tokens del buscador cuentan en el badge y no tienen control
            // acá adentro: esto explica la diferencia (Ajuste #16).
            <p className="m-0 min-w-0 truncate text-[12px] text-text-tertiary">+ {fmt.format(nBusqueda)} de la búsqueda</p>
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={limpiar}
            disabled={!puedeLimpiar}
            focusableWhenDisabled
            className="ml-auto"
          >
            Limpiar
          </Button>
        </div>

        <div key={nLimpiezas} ref={cuerpoRef} className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          {children}
        </div>

        <div className="flex flex-none items-center justify-between gap-3 border-t border-border-subtle px-3 py-2">
          {/* La N va en terciario plano como el resto (artifact `.pop-f`;
              verificación de T3b §4.6): la regla del padre pisa las clases que
              la página le ponga al `<b>`. */}
          <span className="min-w-0 text-[12px] text-text-tertiary [&_b]:font-normal [&_b]:tabular-nums [&_b]:text-text-tertiary">
            {pie}
          </span>
          <PopoverClose render={<Button type="button" />}>Listo</PopoverClose>
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** Una sección del popover: título en versalitas y su id por contexto, para
 *  que los `GrupoChips` de adentro lo usen como `aria-labelledby`. Compacta
 *  (verificación de T3b §4.3): el título a 4 px de su grupo y 12 px entre un
 *  grupo y el título siguiente (los grupos no traen margen propio). */
export function FiltroSeccion({ titulo, separada = false, children }: FiltroSeccionProps) {
  const idTitulo = useId();
  return (
    <>
      {separada && <Separator />}
      <div className="px-3 pb-1.5 pt-1.5">
        <div
          id={idTitulo}
          className="mb-1 text-[10.5px] font-[650] leading-[14px] uppercase tracking-[0.07em] text-text-tertiary"
        >
          {titulo}
        </div>
        <SeccionContexto.Provider value={idTitulo}>{children}</SeccionContexto.Provider>
      </div>
    </>
  );
}
